import { QuillModule } from 'ngx-quill';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { ChangeDetectionStrategy, Component, ViewEncapsulation, Input, ElementRef, inject } from '@angular/core';

import { ReputationWithoutTargetType } from '@sifca-monorepo/widget-generator';

import { MobileReputationComponent } from '../components/reputation/reputation.component';
import { BottomSheetComponent } from '../components';

type ReputationRailState = 'previous' | 'current' | 'locked';

interface ReputationPerkItem {
  icon?: string | null;
  key?: string | null;
  value?: string | null;
}

interface BenefitCardItem {
  description: string;
  icon: string;
  title: string;
}

interface SelectedBenefitsView {
  color: string;
  description: string;
  hasItems: boolean;
  items: BenefitCardItem[];
  showCard: boolean;
  title: string;
}

interface ReputationPageLevelViewModel extends ReputationWithoutTargetType {
  benefitItems: BenefitCardItem[];
  isActive: boolean;
  isBenefitSelected: boolean;
  isLast: boolean;
  isUnlocked: boolean;
  nextRailState: ReputationRailState | null;
  perksDescription: string;
  pictureUrl: string | null;
  railMetricText: string;
  railNoteValueText: string | null;
  railShowLock: boolean;
  railShowsPointsIcon: boolean;
  railState: ReputationRailState;
  resolvedColor: string;
  title: string;
}

interface StatusHeroView {
  currentPointsLabel: string;
  iconUrl: string | null;
  levelLabel: string | null;
  nextTitle: string;
  progressValue: number;
  remainingPointsLabel: string;
  targetPointsLabel: string;
  title: string;
}

interface RailWarningView {
  durationText: string;
  lossLabel: string;
  show: boolean;
}

@Component({
  standalone: true,
  selector: 'reputation-page',
  imports: [CommonModule, QuillModule, TranslatePipe, BottomSheetComponent],
  templateUrl: './reputation-page.component.html',
  styleUrls: ['./reputation-page.component.scss'],
  encapsulation: ViewEncapsulation.ShadowDom,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileReputationPageComponent extends MobileReputationComponent {
  @Input() isWidget = false;
  @Input() darkTheme: { [key: string]: string };
  @Input() cardStyle: { [key: string]: string } = {};
  
  private readonly elementRef = inject(ElementRef);
  private selectedBenefitLevelKey: string | null = null;

  benefitsPopupOpen = false;
  cardInfoStyle: { 'background': string; '--card-text': string; '--card-subtext': string };
  statusAccentColor = '#2563eb';
  isPreLevelBenefitSelected = false;
  reputationRailLevels: ReputationPageLevelViewModel[] = [];
  selectedBenefits: SelectedBenefitsView = this.createEmptySelectedBenefitsView();
  statusHero: StatusHeroView = this.createEmptyStatusHeroView();
  railWarning: RailWarningView = this.createEmptyRailWarningView();

  private updateCardTheme(): void {
    const bg = (this.currentLevel?.color || this.loyaltySettings?.prelevel?.color || '').toString().trim();
    const isDark = this.isDarkColor(bg);
    const text = isDark ? '#ffffff' : '#0f172a';
    const subtext = isDark ? 'rgba(255,255,255,0.86)' : 'rgba(15,23,42,0.72)';

    const hostEl: HTMLElement = this.elementRef.nativeElement;
    hostEl.style.setProperty('--card-text', text);
    hostEl.style.setProperty('--card-subtext', subtext);

    // Keep cardInfoStyle in sync if still needed elsewhere
    this.cardInfoStyle = {
      'background': bg,
      '--card-text': text,
      '--card-subtext': subtext,
    };
  }

  private isDarkColor(input: string): boolean {
    const rgb = this.parseColorToRgb(input);
    if (!rgb) {
      return true;
    }

    const toLinear = (value: number) => {
      const channel = value / 255;
      return channel <= 0.03928 ? channel / 12.92 : Math.pow((channel + 0.055) / 1.055, 2.4);
    };

    const r = toLinear(rgb.r);
    const g = toLinear(rgb.g);
    const b = toLinear(rgb.b);
    const luminance = 0.2126 * r + 0.7152 * g + 0.0722 * b;
    return luminance < 0.4;
  }

  private parseColorToRgb(input: string): { r: number; g: number; b: number } | null {
    if (!input) {
      return null;
    }

    const value = input.trim();
    const hex = value.startsWith('#') ? value.slice(1) : '';

    if (hex) {
      if (hex.length === 3) {
        return {
          r: parseInt(hex[0] + hex[0], 16),
          g: parseInt(hex[1] + hex[1], 16),
          b: parseInt(hex[2] + hex[2], 16),
        };
      }

      if (hex.length === 6 || hex.length === 8) {
        return {
          r: parseInt(hex.slice(0, 2), 16),
          g: parseInt(hex.slice(2, 4), 16),
          b: parseInt(hex.slice(4, 6), 16),
        };
      }
    }

    const rgbMatch = value.match(/^rgba?\(\s*([\d.]+)\s*,\s*([\d.]+)\s*,\s*([\d.]+)(?:\s*,\s*[\d.]+)?\s*\)$/i);
    if (!rgbMatch) {
      return null;
    }

    return {
      r: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[1])))),
      g: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[2])))),
      b: Math.max(0, Math.min(255, Math.round(Number(rgbMatch[3])))),
    };
  }

  protected rebuildReputationLevels(levels: ReputationWithoutTargetType[] = this.reputationLevels): void {
    super.rebuildReputationLevels(levels);

    this.reputationRailLevels = this.attachRailConnectors(
      this.reputationLevels as unknown as ReputationPageLevelViewModel[],
    );

    if (
      this.selectedBenefitLevelKey &&
      !this.reputationRailLevels.some((level) => this.getLevelKey(level) === this.selectedBenefitLevelKey)
    ) {
      this.selectedBenefitLevelKey = null;
    }

    this.updateCardTheme();
    this.updatePageView();
  }

  openBenefitsPopup(): void {
    if (!this.selectedBenefits.description) {
      return;
    }

    this.benefitsPopupOpen = true;
    this.cd.markForCheck();
  }

  closeBenefitsPopup(): void {
    this.benefitsPopupOpen = false;
    this.cd.markForCheck();
  }

  selectBenefitLevel(level: ReputationPageLevelViewModel | null): void {
    this.selectedBenefitLevelKey = level ? this.getLevelKey(level) : null;
    this.updatePageView();
  }

  handleBenefitLevelKeydown(event: KeyboardEvent, level: ReputationPageLevelViewModel | null): void {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    this.selectBenefitLevel(level);
  }

  protected extendReputationLevelViewModel(
    levelViewModel: any,
    level: ReputationWithoutTargetType,
    _index: number,
    _levels: ReputationWithoutTargetType[],
  ): any {
    const walletAmount = Math.max(0, Math.round(+(this.wallet?.amount ?? 0)));
    const absoluteGoal = Math.max(0, Math.round(+(level?.levelInterval?.max ?? 0)));
    const absoluteMin = Math.max(0, Math.round(+(level?.levelInterval?.min ?? 0)));
    const currentRank = this.currentLevel?.rank ?? 0;
    const levelRank = level?.rank ?? 0;
    const railState: ReputationRailState = levelViewModel.isActive
      ? 'current'
      : currentRank && levelRank && levelRank < currentRank
        ? 'previous'
        : 'locked';
    const currentPointsLabel = this.formatPoints(walletAmount);

    return {
      ...levelViewModel,
      benefitItems: this.mapBenefitItems(level?.perks?.items as ReputationPerkItem[] | undefined),
      isBenefitSelected: false,
      isLast: false,
      nextRailState: null,
      perksDescription: level?.perks?.description || '',
      pictureUrl: this.resolvePictureUrl(level?.picture),
      railMetricText:
        railState === 'current'
          ? currentPointsLabel
          : railState === 'previous'
            ? this.formatPoints(absoluteMin)
            : `0 / ${this.formatPoints(absoluteGoal)}`,
      railNoteValueText: railState === 'locked' ? this.formatPoints(Math.max(0, absoluteGoal - walletAmount)) : null,
      railShowLock: railState === 'locked',
      railShowsPointsIcon: true,
      railState,
    };
  }

  private mapBenefitItems(items?: ReputationPerkItem[] | null): BenefitCardItem[] {
    if (!items?.length) {
      return [];
    }

    return items
      .map((item) => {
        const title = this.normalizeBenefitText(item?.key || item?.value || '');
        const description = this.normalizeBenefitText(item?.value || '');

        if (!title && !description) {
          return null;
        }

        return {
          description: description && description !== title ? description : '',
          icon: (item?.icon || '').toString(),
          title: title || description,
        };
      })
      .filter((item): item is BenefitCardItem => !!item);
  }

  private normalizeBenefitText(value: string): string {
    return (value || '')
      .replace(/\s+/g, ' ')
      .replace(/^[\-•\u2022\s]+/, '')
      .trim();
  }

  private resolvePictureUrl(picture?: { baseUrl?: string | null; path?: string | null } | null): string | null {
    if (picture?.baseUrl && picture?.path) {
      return `${picture.baseUrl}/${picture.path}`;
    }

    return null;
  }

  private getLevelKey(level: ReputationPageLevelViewModel): string {
    return level.id || level.reputationLevel || level.title;
  }

  private getActiveReputationLevel(): ReputationPageLevelViewModel | null {
    return this.reputationRailLevels.find((level) => level.isActive) ?? null;
  }

  private resolveSelectedBenefitLevel(): ReputationPageLevelViewModel | null {
    if (this.selectedBenefitLevelKey) {
      return (
        this.reputationRailLevels.find((level) => this.getLevelKey(level) === this.selectedBenefitLevelKey) ??
        this.getActiveReputationLevel()
      );
    }

    return this.getActiveReputationLevel();
  }

  private attachRailConnectors(levels: ReputationPageLevelViewModel[]): ReputationPageLevelViewModel[] {
    return levels.map((level, index) => ({
      ...level,
      isLast: index === levels.length - 1,
      nextRailState: index < levels.length - 1 ? levels[index + 1].railState : null,
    }));
  }

  private createEmptySelectedBenefitsView(): SelectedBenefitsView {
    return {
      color: '#2563eb',
      description: '',
      hasItems: false,
      items: [],
      showCard: false,
      title: '',
    };
  }

  private createEmptyStatusHeroView(): StatusHeroView {
    return {
      currentPointsLabel: '0',
      iconUrl: null,
      levelLabel: null,
      nextTitle: '',
      progressValue: 0,
      remainingPointsLabel: '0',
      targetPointsLabel: '0',
      title: '',
    };
  }

  private createEmptyRailWarningView(): RailWarningView {
    return {
      durationText: '',
      lossLabel: '',
      show: false,
    };
  }

  private updatePageView(): void {
    this.updateStatusHeroView();
    this.updateRailWarningView();
    this.updateSelectedBenefitsView();
    this.updateLevelSelectionState();
    this.cd.markForCheck();
  }

  private updateStatusHeroView(): void {
    const activeLevel = this.getActiveReputationLevel();
    const title = activeLevel?.title || this.preLevelTitle;
    const rank = this.currentLevel?.rank;
    const currentPointsLabel = this.formatPoints(Math.max(0, Math.round(+(this.wallet?.amount ?? 0))));
    const targetPointsLabel = this.isFinalLevel
      ? currentPointsLabel
      : this.formatPoints(
          Math.max(0, Math.round(+(this.nextLevel?.levelInterval?.max ?? this.wallet?.amount ?? 0))),
        );

    this.statusHero = {
      currentPointsLabel,
      iconUrl: activeLevel?.pictureUrl || this.preLevelPictureUrl || this.loyaltyIconSrc,
      levelLabel: rank ? `${this.translate.instant('LEVEL')} ${rank}` : null,
      nextTitle: this.isFinalLevel ? title : this.getLevelTitle(this.nextLevel) || title,
      progressValue: Number.isFinite(this.currentLevelPercentage)
        ? Math.max(0, Math.min(100, this.currentLevelPercentage))
        : 0,
      remainingPointsLabel: this.formatPoints(Math.max(0, Math.round(this.remainingToNextLevel || 0))),
      targetPointsLabel,
      title,
    };
  }

  private updateRailWarningView(): void {
    const warning = this.activeLevelInactivityWarning;

    this.railWarning = {
      durationText: warning?.inactivityText || '',
      lossLabel: this.formatPoints(warning?.lossAmount || 0),
      show: !!warning,
    };
  }

  private updateSelectedBenefitsView(): void {
    const selectedLevel = this.resolveSelectedBenefitLevel();
    const activeLevel = this.getActiveReputationLevel();

    this.statusAccentColor =
      activeLevel?.resolvedColor || this.preLevel?.color || this.nextLevel?.color || '#2563eb';

    const items = selectedLevel
      ? selectedLevel.benefitItems
      : this.mapBenefitItems(this.preLevel?.perks?.items as ReputationPerkItem[] | undefined);

    this.selectedBenefits = {
      color: selectedLevel?.resolvedColor || this.preLevel?.color || '#2563eb',
      description: selectedLevel?.perksDescription || this.preLevel?.perks?.description || '',
      hasItems: items.length > 0,
      items,
      showCard: items.length > 0,
      title: selectedLevel?.title || this.preLevelTitle,
    };
  }

  private updateLevelSelectionState(): void {
    const activeLevel = this.getActiveReputationLevel();
    const selectedKey = this.selectedBenefitLevelKey ?? (activeLevel ? this.getLevelKey(activeLevel) : null);

    this.isPreLevelBenefitSelected = !this.selectedBenefitLevelKey && !activeLevel;

    this.reputationRailLevels = this.reputationRailLevels.map((level) => ({
      ...level,
      isBenefitSelected: !!selectedKey && this.getLevelKey(level) === selectedKey,
    }));
  }
}
